import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Comparison {
  id: string;
  name: string;
  rating: number;
  price: string;
  features: string;
  best_for: string;
  image: string;
  sort_order: number;
  pros: { id: string; text: string }[];
  cons: { id: string; text: string }[];
}

interface ComparisonForm {
  name: string;
  rating: string;
  price: string;
  features: string;
  best_for: string;
  image: string;
  sort_order: string;
}

const emptyForm: ComparisonForm = { name: '', rating: '0', price: '', features: '', best_for: '', image: '', sort_order: '0' };

export default function ComparisonsManager() {
  const [items, setItems] = useState<Comparison[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ComparisonForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [prosText, setProsText] = useState('');
  const [consText, setConsText] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data: comps, error: compError } = await supabase.from('comparison_products').select('*').order('sort_order');
      if (compError) throw compError;
      const withDetails = await Promise.all(
        (comps || []).map(async (c) => {
          const [{ data: pros }, { data: cons }] = await Promise.all([
            supabase.from('comparison_pros').select('id,text').eq('comparison_id', c.id).order('sort_order'),
            supabase.from('comparison_cons').select('id,text').eq('comparison_id', c.id).order('sort_order'),
          ]);
          return { ...c, pros: pros || [], cons: cons || [] };
        })
      );
      setItems(withDetails);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setProsText(''); setConsText(''); setShowForm(true); };
  const openEdit = (c: Comparison) => {
    setEditingId(c.id);
    setForm({ name: c.name, rating: String(c.rating), price: c.price, features: c.features, best_for: c.best_for, image: c.image, sort_order: String(c.sort_order) });
    setProsText(c.pros.map(p => p.text).join('\n'));
    setConsText(c.cons.map(cn => cn.text).join('\n'));
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this comparison?')) return;
    try {
      await supabase.from('comparison_products').delete().eq('id', id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, rating: parseFloat(form.rating) || 0, price: form.price,
      features: form.features, best_for: form.best_for, image: form.image,
      sort_order: parseInt(form.sort_order) || 0,
    };
    try {
      let compId = editingId;
      if (editingId) {
        await supabase.from('comparison_products').update(payload).eq('id', editingId);
      } else {
        const { data: newComp, error: insError } = await supabase.from('comparison_products').insert(payload).select('id').single();
        if (insError) throw insError;
        compId = newComp?.id;
      }

      if (compId) {
        await supabase.from('comparison_pros').delete().eq('comparison_id', compId);
        await supabase.from('comparison_cons').delete().eq('comparison_id', compId);

        const proLines = prosText.split('\n').filter(l => l.trim());
        const conLines = consText.split('\n').filter(l => l.trim());

        if (proLines.length > 0) {
          await supabase.from('comparison_pros').insert(
            proLines.map((text, i) => ({ comparison_id: compId, text: text.trim(), sort_order: i + 1 }))
          );
        }
        if (conLines.length > 0) {
          await supabase.from('comparison_cons').insert(
            conLines.map((text, i) => ({ comparison_id: compId, text: text.trim(), sort_order: i + 1 }))
          );
        }
      }

      setShowForm(false);
      fetchItems();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-20 text-center text-ink-500">Loading...</div>;
  if (error) return <div className="py-20 text-center"><p className="text-rose-600 text-sm mb-3">{error}</p><button onClick={fetchItems} className="h-9 px-4 rounded-lg bg-ink-900 text-white text-sm font-semibold whitespace-nowrap">Retry</button></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold text-ink-900">Comparisons ({items.length})</h2>
        <button onClick={openAdd} className="h-9 px-4 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-sm font-semibold whitespace-nowrap flex items-center gap-1.5 transition">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
          Add Comparison
        </button>
      </div>

      <div className="bg-white rounded-xl border border-ink-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-ink-50/70 border-b border-ink-100">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Product</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Rating</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Price</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Best For</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Pros / Cons</th>
                <th className="text-right text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className="border-b border-ink-100 last:border-b-0 hover:bg-ink-50/50 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {c.image && (
                        <div className="w-10 h-10 rounded-lg bg-ink-100 overflow-hidden flex-shrink-0">
                          <img src={c.image} alt={c.name} className="w-full h-full object-cover object-top" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-semibold text-ink-900">{c.name}</p>
                        <p className="text-xs text-ink-500">{c.features}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-star-fill text-amber-500 text-xs"></i></span>
                      <span className="text-sm font-semibold text-ink-800">{c.rating}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-ink-900">{c.price}</td>
                  <td className="px-4 py-3 text-sm text-ink-600">{c.best_for}</td>
                  <td className="px-4 py-3 text-xs text-ink-500">
                    <span className="text-emerald-600">+{c.pros.length} pros</span>
                    <span className="mx-1">·</span>
                    <span className="text-rose-600">-{c.cons.length} cons</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(c)} className="w-8 h-8 rounded-lg hover:bg-ink-100 text-ink-600 flex items-center justify-center transition"><i className="ri-edit-line text-sm"></i></button>
                      <button onClick={() => handleDelete(c.id)} className="w-8 h-8 rounded-lg hover:bg-rose-50 text-ink-600 hover:text-rose-600 flex items-center justify-center transition"><i className="ri-delete-bin-line text-sm"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
          <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" onClick={() => setShowForm(false)}></div>
          <div className="relative bg-white rounded-2xl border border-ink-100 shadow-card w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-ink-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-ink-900">{editingId ? 'Edit Comparison' : 'Add Comparison'}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-lg hover:bg-ink-50 flex items-center justify-center text-ink-500 transition"><i className="ri-close-line"></i></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Rating</label>
                  <input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={e => setForm({ ...form, rating: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Price</label>
                  <input value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" placeholder="$328" required />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Features</label>
                <input value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Best For</label>
                <input value={form.best_for} onChange={e => setForm({ ...form, best_for: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Image URL</label>
                <input value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Pros (one per line)</label>
                <textarea value={prosText} onChange={e => setProsText(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition resize-none font-mono text-xs" placeholder="Best-in-class ANC&#10;Excellent call quality" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Cons (one per line)</label>
                <textarea value={consText} onChange={e => setConsText(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition resize-none font-mono text-xs" placeholder="Not foldable&#10;No IP rating" />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="h-9 px-4 rounded-lg border border-ink-200 text-sm font-semibold text-ink-600 hover:bg-ink-50 whitespace-nowrap transition">Cancel</button>
                <button type="submit" disabled={saving} className="h-9 px-4 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-sm font-semibold whitespace-nowrap transition disabled:opacity-50">{saving ? 'Saving...' : editingId ? 'Save' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}